import * as THREE from 'three';
import CameraControls from 'camera-controls';
import {
	DEFAULT_MAX_AZIMUTH_ANGLE,
	DEFAULT_MAX_POLAR_ANGLE,
	DEFAULT_MIN_AZIMUTH_ANGLE,
	DEFAULT_MIN_POLAR_ANGLE,
} from '@3jss/utils/constants';
import { MathUtils } from 'three';

CameraControls.install({ THREE });

/**
 * CustomCameraControls extends the CameraControls class and provides
 * additional functionality for custom rotation speeds, auto-rotation,
 * damping, and configuration-based updates.
 */
class CustomCameraControls extends CameraControls {
	/**
	 * Constructs a new instance of the class, initializing it with the specified camera, DOM element, and configuration.
	 *
	 * @param {Object} camera - The camera object to be used.
	 * @param {HTMLElement} domElement - The DOM element to attach the controls to.
	 * @param {Object} config - The configuration object for initializing various settings.
	 * @return {void}
	 */
	constructor(camera, domElement, config) {
		super(camera, domElement);

		this._config = config;

		this.azimuthRotateSpeed = -1;
		this.polarRotateSpeed = -1;

		this._enableAutoRotate = false;
		this._autoRotateSpeed = 20;
		this._cancelAutoRotateOnInteraction = true;
		this._resetSceneOnInteraction = true;
	}

	/**
	 * Sets the damping properties to control the smoothness of dragging behavior.
	 *
	 * @function setDamping
	 * @param {Object} config - Configuration object for damping.
	 * @param {boolean} config.enable - A flag to enable or disable the damping effect.
	 * @param {number} config.factor - The factor that determines the smooth time for dragging when damping is enabled.
	 */
	setDamping = ({ enable, factor }) => {
		if (enable) {
			this.draggingSmoothTime = factor;
		} else {
			this.draggingSmoothTime = 0;
		}
	};

	/**
	 * Updates the camera configuration and settings based on the provided configuration object.
	 *
	 * @param {Object} config - The configuration options for the camera.
	 * @param {string} config.sceneType - The type of scene being rendered (e.g., "flat_scene").
	 * @param {boolean} [config.camera_rotation=false] - Whether the camera supports rotation.
	 * @param {number} [config.min_horizontal_angle=DEFAULT_MIN_AZIMUTH_ANGLE] - The minimum horizontal angle in degrees.
	 * @param {number} [config.max_horizontal_angle=DEFAULT_MAX_AZIMUTH_ANGLE] - The maximum horizontal angle in degrees.
	 * @param {number} [config.starting_cam_vertical_angle=90] - The starting vertical camera angle in degrees.
	 * @param {number} [config.min_vertical_angle=DEFAULT_MIN_POLAR_ANGLE] - The minimum vertical angle in degrees.
	 * @param {number} [config.max_vertical_angle=DEFAULT_MAX_POLAR_ANGLE] - The maximum vertical angle in degrees.
	 * @param {number} [config.starting_cam_horizontal_angle=90] - The starting horizontal camera angle in degrees.
	 * @param {any} [config.zoom] - The initial zoom level for the camera.
	 * @param {boolean} [config.smooth=false] - Whether the camera movement should be smooth.
	 * @param {Object} [config.config] - Additional configuration options.
	 */
	updateFromConfig = ({
		sceneType,
		camera_rotation = false,
		min_horizontal_angle = DEFAULT_MIN_AZIMUTH_ANGLE,
		max_horizontal_angle = DEFAULT_MAX_AZIMUTH_ANGLE,
		starting_cam_vertical_angle = 90,
		min_vertical_angle = DEFAULT_MIN_POLAR_ANGLE,
		max_vertical_angle = DEFAULT_MAX_POLAR_ANGLE,
		starting_cam_horizontal_angle = 90,
		zoom,
		smooth = false,
		reset = true,
		...config
	}) => {
		this.minPolarAngle = MathUtils.degToRad(min_vertical_angle);
		this.maxPolarAngle = MathUtils.degToRad(max_vertical_angle);

		this.minAzimuthAngle =
			min_horizontal_angle === 0
				? -Infinity
				: MathUtils.degToRad(min_horizontal_angle);
		this.maxAzimuthAngle =
			max_horizontal_angle >= 359
				? Infinity
				: MathUtils.degToRad(max_horizontal_angle);

		sceneType !== 'flat_scene' &&
			reset &&
			this.rotateTo(
				MathUtils.degToRad(starting_cam_horizontal_angle),
				MathUtils.degToRad(starting_cam_vertical_angle),
				smooth,
			);
	};

	/**
	 * Handles the automatic rotation step based on the current auto rotation speed and delta time.
	 *
	 * @function _autoRotateStep
	 * @param {number} delta - The time elapsed since the last update, used to adjust the rotation step.
	 * @private
	 */
	_autoRotateStep = (delta) => {
		if (this._autoRotateSpeed) {
			this.azimuthAngle +=
				this._autoRotateSpeed * delta * THREE.MathUtils.DEG2RAD;
		}
	};

	/**
	 * Cancels the auto-rotation behavior by disabling it and updating the configuration.
	 *
	 * @function _cancelAutoRotation
	 * @private
	 */
	_cancelAutoRotation = () => {
		if (this._enableAutoRotate) {
			this.setupAutoRotate({
				enabled: false,
			});
			this.updateFromConfig({
				...this._config,
				smooth: true,
				reset: this._resetSceneOnInteraction,
			});
		}
	};

	/**
	 * Configures the automatic rotation feature with options such as speed, direction, and delay.
	 *
	 * @function setupAutoRotate
	 * @param {Object} options - Configuration options for auto-rotation.
	 * @param {boolean} options.enabled - Whether auto-rotation should be enabled.
	 * @param {number} [options.autoRotateSpeed=2.0] - The speed of auto-rotation.
	 * @param {string} [options.autoRotateDirection='left'] - The direction of auto-rotation, either 'left' or 'right'.
	 * @param {number} [options.autoRotateDelay=0] - The delay in seconds before auto-rotation starts.
	 * @param {boolean} [options.cancelAutoRotateOnInteraction=true] - Whether auto-rotation should stop on user interaction.
	 */
	setupAutoRotate = ({
		enabled,
		autoRotateSpeed = 2.0,
		autoRotateDirection = 'left',
		autoRotateDelay = 0,
		cancelAutoRotateOnInteraction = true,
		resetSceneOnInteraction = true,
	}) => {
		if (!enabled) {
			this._enableAutoRotate = enabled;
		} else {
			setTimeout(() => {
				this._enableAutoRotate = enabled;
				this.minAzimuthAngle = MathUtils.degToRad(-Infinity);
				this.maxAzimuthAngle = MathUtils.degToRad(Infinity);

				this._autoRotateSpeed =
					(autoRotateDirection === 'right'
						? -autoRotateSpeed
						: autoRotateSpeed) * 10;

				this._cancelAutoRotateOnInteraction =
					cancelAutoRotateOnInteraction;
				this._resetSceneOnInteraction = resetSceneOnInteraction;
				if (this._cancelAutoRotateOnInteraction) {
					window.addEventListener('wheel', this._cancelAutoRotation, {
						passive: true,
					});
					window.addEventListener(
						'pointerup',
						this._cancelAutoRotation,
						{ passive: true },
					);
				}
			}, autoRotateDelay * 1000);
		}
	};

	/**
	 * Updates the camera controls, applying the auto-rotation step if enabled.
	 *
	 * @function update
	 * @param {number} delta - The time elapsed since the last update, used for animations and auto-rotation.
	 * @returns {boolean} - Returns the update result from the parent class.
	 */
	update = (delta) => {
		if (this._enableAutoRotate) this._autoRotateStep(delta);
		return super.update(delta);
	};

	/**
	 * Rotates the camera upward by the specified angle.
	 *
	 * @function rotateUp
	 * @param {number} angle - The angle by which to rotate the camera upward.
	 */
	rotateUp = (angle) => {
		this.rotate(0, angle, true);
	};

	/**
	 * Rotates the camera to the left by the specified angle.
	 *
	 * @function rotateLeft
	 * @param {number} angle - The angle by which to rotate the camera to the left.
	 */
	rotateLeft = (angle) => {
		this.rotate(angle, 0, true);
	};

	// TODO: Investigate
	// _collisionTest() {
	//
	// 	let distance = Infinity;
	//
	// 	const _ray = new THREE.Ray();
	//
	// 	if (!this.storeCollider) return distance;
	// 	let _v3A = new THREE.Vector3();
	// 	let _v3B = new THREE.Vector3();
	// 	let _v3C = new THREE.Vector3();
	// 	let _rotationMatrix = new THREE.Matrix4();
	// 	let _ORIGIN = Object.freeze(new THREE.Vector3(0, 0, 0));
	//
	// 	const direction = _v3A.setFromSpherical( this._spherical ).divideScalar( this._spherical.radius );
	// 	_rotationMatrix.lookAt( _ORIGIN, direction, this._camera.up );
	//
	// 	for ( let i = 0; i < 4; i ++ ) {
	//
	// 		const nearPlaneCorner = _v3B.copy( this._nearPlaneCorners[ i ] );
	// 		nearPlaneCorner.applyMatrix4( _rotationMatrix );
	//
	// 		const origin = _v3C.addVectors( this._target, nearPlaneCorner );
	// 		_ray.set( origin, direction );
	//
	// 		let intersect = this.storeCollider.geometry.boundsTree.raycastFirst(_ray);
	//
	// 		if ( intersect && intersect.distance < distance ) {
	//
	// 			distance = intersect.distance;
	//
	// 		}
	//
	// 	}
	//
	// 	return distance;
	//
	// }
}

export default CustomCameraControls;
